import React, { useState } from "react";
import { Check, Copy } from "lucide-react";

/**
 * prompt-kit style CodeBlock: bordered card with a header row (language label
 * + copy button) and a scrollable monospace code area. Mirrors prompt-kit's
 * CodeBlock / CodeBlockCode API so steps and messages share one look.
 *
 *   <CodeBlock>
 *     <CodeBlockCode code={code} language="javascript" />
 *   </CodeBlock>
 */
export function CodeBlock({ children, className = "" }) {
  return (
    <div
      className={`not-prose flex w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white ${className}`}
    >
      {children}
    </div>
  );
}

export function CodeBlockCode({ code = "", language = "", className = "" }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };

  return (
    <>
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
          {language}
        </span>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre
        className={`max-h-72 overflow-auto p-3 text-[11px] leading-5 text-gray-800 ${className}`}
      >
        <code className="font-mono">{code}</code>
      </pre>
    </>
  );
}

export default CodeBlock;
